import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Platform, StyleSheet, View, type ViewInstance } from 'react-native';
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated';

import {
  AnimatedViews,
  ScreenHeader as GarnishScreenHeader,
  theme,
  useElementVisibility,
  useIsFocused,
  useNavbarContainerHeight,
  useResponsive,
  VStack,
  webOnlyStyles,
} from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useGroupOrderIntroState, useGroupOrderingContent } from '@order/features-group-ordering';
import {
  getBannerContent,
  type HomeContent,
  HomeProductCategory,
  Home,
  useHomeContainerSpacing,
  useHomeContent,
  useHomeFiltering,
  useHomeLastCanceledOrder,
  useHomeMenu,
  useHomePendingFeedbackOrder,
} from '@order/features-home';
import { FlexEntryPoint, useFlexEntryPoint } from '@order/features-payments';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useDeliveryDisclosure } from '@order/shared-hooks';
import { useTelemetry } from '@order/Telemetry';

import {
  AppFooter,
  FloatingNotice,
  MenuReorderRail,
  ScreenHeader,
  useFloatingNotice,
  useMenuContent,
} from '../../../../components';
import { GroupOrderIntroModal } from '../../../../components/GroupOrderIntroModal';
import {
  getLastInteractedDeliveryDetails,
  useDietaryPreferencesMachine,
  useLastInteractedStore,
  useLastInteractedStoreMenuHref,
} from '../../../../context';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  useIsLiquidGlassEnabled,
  useLastOrderStatus,
  useMenuReorderRailOrders,
  useNoticeToastBottomOffset,
  useOneClickAddToBag,
  usePendingOrder,
} from '../../../../hooks';
import { isPendingOrder, isPendingOrderStatus } from '../../utils';
import { HomeScreenPostOrder } from './HomeScreenPostOrder';

export const HomeScreenContent = () => {
  const isLoggedIn = useIsLoggedIn();
  const isFocused = useIsFocused();
  const router = useRouter();
  const { orderId: placedOrderId } = useLocalSearchParams<{ orderId?: string }>();
  const { track } = useTelemetry();
  const { currentBreakpoint: { isXS, isLG }, match } = useResponsive(); // prettier-ignore

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  // ─── Group Order Intro ─────────────────────────────────────────────────────

  const handleIntroComplete = useCallback(() => {
    router.navigate('/group-ordering/create');
  }, [router]);

  const handleIntroShow = useCallback(() => {
    track('group_ordering.intro.shown');
  }, [track]);

  const {
    visible: isIntroVisible,
    show: showIntro,
    hide: hideIntro,
    dismiss: dismissIntro,
  } = useGroupOrderIntroState({
    onShow: handleIntroShow,
    onComplete: handleIntroComplete,
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerSpacingStyle = useHomeContainerSpacing();
  const { offsetStyle: navbarOffsetStyle } = useNavbarContainerHeight();

  // So that the banner takes up 70% of the height of the screen on larger viewports.
  const bannerHeight = match([null, webOnlyStyles({ minHeight: '70svh' as never })]);

  const bottomBannersContainerStyles = [
    styles.bottomBannersContainer,
    isXS ? [styles.bottomBannersContainerXS, navbarOffsetStyle] : undefined,
  ];

  // ─── Location ──────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore() ?? {};
  const { restaurantId, restaurantSlug = '', restaurantName, addressId } = lastInteractedStore;
  const deliveryDetails = getLastInteractedDeliveryDetails(lastInteractedStore);
  const hasLocation = restaurantSlug || deliveryDetails?.addressId;
  const menuHref = useLastInteractedStoreMenuHref();

  // ─── Recent Orders ─────────────────────────────────────────────────────────

  const {
    orders,
    mostRecentLineItems,
    mostRecentOrder,
    hasMoreLineItemsToSee,
    isFetchingMostRecentOrders,
  } = useMenuReorderRailOrders({
    restaurantId: Number.isNaN(Number(restaurantId)) ? 0 : Number(restaurantId),
    enabled: isLoggedIn && Boolean(restaurantId),
    maxItems: 5,
  });

  // ─── Pending Order ───────────────────────────────────────────────────────
  // Loads the last order and its order status from cache (requested on home screen).

  const lastOrderStatus = useLastOrderStatus({ requestPolicy: 'cache-only' });
  const isLoadingLastOrderStatus = lastOrderStatus?.isLoadingLastOrder;
  const hasPendingOrder = isPendingOrderStatus(lastOrderStatus?.orderStatus);

  // ─── Multiple Pending Orders ───────────────────────────────────────────────
  // Extra requirement to show when there are multiple pending orders.

  const pendingOrders = useMemo(() => orders.filter(isPendingOrder), [orders]);
  const hasMultiplePendingOrders = pendingOrders.length > 1;
  const isHomePostOrder = hasPendingOrder || hasMultiplePendingOrders;

  // ─── Menu ──────────────────────────────────────────────────────────────────
  // The post-order view doesn't use the menu, so it only loads outside of it.

  const { restaurant, deliveryFee, products, isLoadingMenu } = useHomeMenu({
    restaurantId,
    addressId,
    pause: isHomePostOrder,
  });
  const { deliveryDisclosure } = useDeliveryDisclosure({
    restaurantDeliveryFee: deliveryFee,
  });
  const pendingOrder = usePendingOrder({
    requestPolicy: 'cache-and-network',
    withPolling: true,
    placedOrderId,
  });

  // ─── Allergens ─────────────────────────────────────────────────────────────

  const { restrictions: dietaryPreferences } = useDietaryPreferencesMachine();

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<HomeContent>({ contentType: 'homeScreen', include: 2 }); // prettier-ignore
  const content = useHomeContent(entry?.data?.fields);
  const isLoadingContent = entry?.fetching;

  const { mappedContent: groupOrderingContent } = useGroupOrderingContent({ pause: !isFocused });
  const { notice, dismissNotice } = useFloatingNotice();

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const { mappedContent: { caloriesOverrides } } = useMenuContent(); // prettier-ignore
  const { filterableCategories, genericCategories } = useHomeFiltering({
    products,
    filterableCategories: content.filterableCategories,
    genericCategories: isLoggedIn ? content.genericCategories : content.loggedOutCategories,
    caloriesOverrides,
  });

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const filterableCategoriesRef = useRef<ViewInstance>(null);
  const genericCategoriesRef = useRef<ViewInstance>(null);

  // ─── State ─────────────────────────────────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<HomeProductCategory | null>(null);
  const hasSelectedCategory = Boolean(selectedCategory) && filterableCategories.length > 0;

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const shouldRenderGenericCategories = genericCategories.length > 0 && !hasSelectedCategory;
  const shouldRenderFallbackCategories = genericCategories.length === 0 && !hasSelectedCategory;

  const isFilterableCategoriesVisible = useElementVisibility(filterableCategoriesRef, {
    isEnabled: !isXS && hasSelectedCategory,
  });
  const isGenericCategoriesVisible = useElementVisibility(genericCategoriesRef, {
    isEnabled: !isXS && shouldRenderGenericCategories,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCategorySelection = useCallback(
    (category: HomeProductCategory | null) => {
      track('home.filter.selected', { filterName: category?.categoryName ?? null });
      setSelectedCategory(category);
    },
    [track],
  );

  // ─── Reorder ───────────────────────────────────────────────────────────────

  // Filtering should only hide the reorder rail on XS.
  const shouldHideContentOnXS = Boolean(selectedCategory) && isXS;

  const canRenderReorderRail =
    !shouldHideContentOnXS &&
    restaurantId &&
    hasLocation &&
    restaurantName &&
    mostRecentLineItems.length > 0;

  // ─── Last Canceled Order ───────────────────────────────────────────────────

  const { lastCanceledOrderId, hasRecentCanceledOrder } = useHomeLastCanceledOrder({ pause: !isLoggedIn || !isFocused }); // prettier-ignore

  const hasStickyCanceledOrderBanner = hasRecentCanceledOrder && isXS;

  // ─── Pending Feedback ──────────────────────────────────────────────────────

  const { rateableOrder, isLoadingFeedback } = useHomePendingFeedbackOrder({
    orderId: mostRecentOrder?.id,
    isLoggedIn,
  });

  const canRenderRateOrderCard = isLoggedIn && rateableOrder && !shouldHideContentOnXS;

  // ─── Flex ──────────────────────────────────────────────────────────────────

  const { shouldRenderFlexEntryPoint, checkFlexEligibility, dismissFlexEntryPoint } = useFlexEntryPoint({ isLoggedIn }); // prettier-ignore

  const shouldRenderFloatingBanners = isXS && (notice !== undefined || shouldRenderFlexEntryPoint);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { addProductToBag } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails,
  });

  const handleGroupOrderCtaPress = useCallback(() => {
    track('group_ordering.entry_point.tapped');
    showIntro();
  }, [showIntro, track]);

  const handleGroupOrderIntroPrimaryCtaPress = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
  }, [track]);

  const handleGroupOrderIntroSecondaryCtaPress = useCallback(() => {
    track('group_ordering.intro.secondary_cta.tapped');
    track('group_ordering.intro.dismissed');

    dismissIntro();
  }, [dismissIntro, track]);

  const handleStartGroupOrder = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
    track('group_ordering.intro.dismissed');

    hideIntro();
  }, [hideIntro, track]);

  const openMenu = useCallback(() => {
    router.navigate(hasLocation ? '/menu' : '/location-selection');
  }, [router, hasLocation]);

  const trackMenuView = useCallback(() => {
    track('home.view_menu.selected');
  }, [track]);

  const openProductDetails = useCallback(
    (slug: string) => {
      const productSlug = slug;
      const addressId = deliveryDetails?.addressId;

      if (addressId && productSlug) {
        router.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId, productSlug },
        });
        return;
      }

      if (restaurantSlug && productSlug) {
        router.navigate({
          pathname: '/[restaurantSlug]/[productSlug]',
          params: { restaurantSlug, productSlug },
        });
        return;
      }

      openMenu();
    },
    [deliveryDetails?.addressId, restaurantSlug, router, openMenu],
  );

  const handleAddProductToBag = useCallback(
    async (id: string, name: string) => {
      if (!hasLocation) {
        openMenu();
        return;
      }

      track('home.product.added', { id, name });

      return await addProductToBag({ id, name }, 1);
    },
    [addProductToBag, hasLocation, openMenu, track],
  );

  const viewCanceledOrder = useCallback(() => {
    if (!lastCanceledOrderId) return;

    router.navigate(`/order-status/${lastCanceledOrderId}`);
  }, [router, lastCanceledOrderId]);

  const rateOrder = useCallback(() => {
    if (!rateableOrder) return;

    track('home.post_order.rate_order.selected');
    router.navigate(`/feedback/${rateableOrder.id}`);
  }, [router, track, rateableOrder]);

  const trackItemSelection = useCallback(
    (product: string, category: string) => {
      track('home.product.selected', { product, category });
    },
    [track],
  );

  const disclaimer =
    addressId && restaurant?.showDeliveryFeeDisclosure ? deliveryDisclosure : undefined;

  const isProductCategoryVisible = isFilterableCategoriesVisible === true || isGenericCategoriesVisible === true; // prettier-ignore

  const shouldShowDisclaimer = Platform.select({
    web: Boolean(disclaimer && isProductCategoryVisible),
    default: Boolean(disclaimer),
  });

  // ─── Notice Toasts ─────────────────────────────────────────────────────────

  const homeBottomNotices = isXS
    ? [notice, shouldRenderFlexEntryPoint]
    : [notice, shouldShowDisclaimer, hasRecentCanceledOrder];
  const bottomNotices = isHomePostOrder ? [notice] : homeBottomNotices;
  const bottomNoticesCount = bottomNotices.filter(Boolean).length;

  useNoticeToastBottomOffset({ offset: getToastBottomOffset({ bottomNoticesCount }) });

  // ───────────────────────────────────────────────────────────────────────────

  const footerStyles = useMemo(() => {
    if (hasRecentCanceledOrder && disclaimer) {
      return styles.footerWithTwoBottomNotices;
    }

    if (hasRecentCanceledOrder || disclaimer) {
      return styles.footerWithOneBottomNotice;
    }
  }, [disclaimer, hasRecentCanceledOrder]);

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoadingContent || isLoadingMenu || isFetchingMostRecentOrders || isLoadingLastOrderStatus) {
    return (
      <>
        {isXS ? <ScreenHeader pendingOrder={pendingOrder} rounded={false} /> : null}

        <Home.Loading
          size={isLoggedIn ? 'regular' : 'large'}
          bannerSize={isLoggedIn ? 'small' : 'large'}
          bannerStyle={bannerHeight}
        />
      </>
    );
  }

  if (isHomePostOrder) {
    return (
      <>
        <HomeScreenPostOrder
          content={content}
          pendingOrder={pendingOrder}
          lastOrderStatus={lastOrderStatus}
          pendingOrders={pendingOrders}
          hasPendingOrder={hasPendingOrder}
          hasMultiplePendingOrders={hasMultiplePendingOrders}
        />

        {notice ? (
          <View style={bottomBannersContainerStyles}>
            <FloatingNotice text={notice.text} palette={notice.palette} onClose={dismissNotice} />
          </View>
        ) : null}
      </>
    );
  }

  return (
    <>
      {isXS ? (
        <ScreenHeader
          disclaimer={disclaimer}
          pendingOrder={pendingOrder}
          rounded={false}
          shouldShowLocationTooltip
        />
      ) : null}

      <AnimatedViews.Reveal style={styles.contentContainer}>
        <Home.Container stickyHeaderIndices={hasStickyCanceledOrderBanner ? [0] : []}>
          {hasStickyCanceledOrderBanner ? (
            <Home.OrderCanceledBanner onPress={viewCanceledOrder} />
          ) : null}

          <Home.Container.Content>
            <Home.Banner
              style={bannerHeight}
              size={isLoggedIn ? 'small' : 'large'}
              content={getBannerContent(content, isLoggedIn, isXS)}
              secondaryCtaLabel={
                isGroupOrderingEnabled ? groupOrderingContent.entryPointCtaLabel : undefined
              }
              onSecondaryCtaPress={handleGroupOrderCtaPress}
            />

            {canRenderReorderRail && !isXS ? (
              <Home.ReorderRail.Container>
                {isLG ? (
                  <ReorderRail.Header style={styles.reorderRailHeader}>
                    <ReorderRail.Heading />
                  </ReorderRail.Header>
                ) : (
                  <ReorderRail.Header>
                    <Home.ReorderRail.SideWrapper />

                    <ReorderRail.Heading />

                    <Home.ReorderRail.SideWrapper>
                      {hasMoreLineItemsToSee ? (
                        <MenuReorderRail.SeeAll
                          restaurantId={restaurantId}
                          deliveryDetails={deliveryDetails}
                        />
                      ) : null}
                    </Home.ReorderRail.SideWrapper>
                  </ReorderRail.Header>
                )}

                <MenuReorderRail.List
                  location="home"
                  contentContainerStyle={[containerSpacingStyle, styles.reorderRailList]}
                  lineItems={mostRecentLineItems}
                  mostRecentOrder={mostRecentOrder}
                  restaurantId={restaurantId}
                  restaurantName={restaurantName}
                  restaurantSlug={restaurantSlug}
                  deliveryDetails={deliveryDetails}
                >
                  {hasMoreLineItemsToSee && isLG ? (
                    <View style={styles.seeAllItem}>
                      <MenuReorderRail.SeeAll
                        restaurantId={restaurantId}
                        deliveryDetails={deliveryDetails}
                      />
                    </View>
                  ) : null}
                </MenuReorderRail.List>
              </Home.ReorderRail.Container>
            ) : null}

            {!isXS && canRenderRateOrderCard ? (
              <Home.RateOrderCard
                image={rateableOrder.lineItems[0]?.product?.asset?.url}
                orderCount={rateableOrder.lineItems.length}
                isLoading={isLoadingFeedback}
                onPress={rateOrder}
              />
            ) : null}

            {(canRenderReorderRail || canRenderRateOrderCard) && !isXS ? <Home.Divider /> : null}

            {filterableCategories.length > 0 ? (
              <Home.FilterableCategories
                ref={filterableCategoriesRef}
                title={content.filtersTitle}
                filterableCategories={filterableCategories}
                dietaryPreferences={dietaryPreferences}
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategorySelection}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={handleAddProductToBag}
                trackItemSelection={trackItemSelection}
              />
            ) : null}

            {isXS && canRenderReorderRail ? (
              <Home.ReorderRail.Container>
                <ReorderRail.Header style={containerSpacingStyle}>
                  <ReorderRail.Heading />

                  {hasMoreLineItemsToSee ? (
                    <MenuReorderRail.SeeAll
                      restaurantId={restaurantId}
                      deliveryDetails={deliveryDetails}
                    />
                  ) : null}
                </ReorderRail.Header>

                <MenuReorderRail.List
                  disableAnimation={Platform.OS !== 'web'}
                  location="home"
                  contentContainerStyle={[containerSpacingStyle, styles.reorderRailList]}
                  lineItems={mostRecentLineItems}
                  mostRecentOrder={mostRecentOrder}
                  restaurantId={restaurantId}
                  restaurantName={restaurantName}
                  restaurantSlug={restaurantSlug}
                  deliveryDetails={deliveryDetails}
                />
              </Home.ReorderRail.Container>
            ) : null}

            {isXS && canRenderRateOrderCard ? (
              <Home.RateOrderCard
                image={rateableOrder.lineItems[0]?.product?.asset?.url}
                orderCount={rateableOrder.lineItems.length}
                isLoading={isLoadingFeedback}
                onPress={rateOrder}
              />
            ) : null}

            {(canRenderReorderRail || canRenderRateOrderCard) && isXS ? <Home.Divider /> : null}

            {shouldRenderGenericCategories ? (
              <Home.Container.AnimatedContent>
                <Home.GenericCategories
                  ref={genericCategoriesRef}
                  size={isLoggedIn ? 'regular' : 'large'}
                  genericCategories={genericCategories}
                  dietaryPreferences={dietaryPreferences}
                  openMenu={openMenu}
                  openProductDetails={openProductDetails}
                  addProductToBag={handleAddProductToBag}
                  trackItemSelection={trackItemSelection}
                />
              </Home.Container.AnimatedContent>
            ) : null}

            {shouldRenderFallbackCategories ? (
              <Home.FallbackCategories
                size={isLoggedIn ? 'regular' : 'large'}
                dietaryPreferences={dietaryPreferences}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={handleAddProductToBag}
                trackItemSelection={trackItemSelection}
              />
            ) : null}

            <Home.Footer
              menuHref={menuHref}
              title={content.footerTitle}
              imageUrl={content.footerImageUrl}
              imageA11y={content.footerImageA11y}
              trackMenuView={trackMenuView}
            />
          </Home.Container.Content>

          {isXS ? null : <AppFooter style={footerStyles} />}
        </Home.Container>
      </AnimatedViews.Reveal>

      {shouldRenderFloatingBanners ? (
        <VStack gap={FLOATING_BANNERS_GAP} style={bottomBannersContainerStyles}>
          {shouldRenderFlexEntryPoint ? (
            <FlexEntryPoint
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              onCheckEligibility={checkFlexEligibility}
              onDismiss={dismissFlexEntryPoint}
            />
          ) : null}

          {notice ? (
            <FloatingNotice text={notice.text} palette={notice.palette} onClose={dismissNotice} />
          ) : null}
        </VStack>
      ) : null}

      {!isXS ? (
        <View style={styles.bottomBannersContainer}>
          {notice ? (
            <FloatingNotice text={notice.text} palette={notice.palette} onClose={dismissNotice} />
          ) : null}

          {shouldShowDisclaimer && disclaimer ? (
            <Animated.View
              entering={DISCLAIMER_ENTERING_ANIMATION}
              exiting={DISCLAIMER_EXITING_ANIMATION}
            >
              <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} />
            </Animated.View>
          ) : null}

          {hasRecentCanceledOrder ? <Home.OrderCanceledBanner onPress={viewCanceledOrder} /> : null}
        </View>
      ) : null}

      <GroupOrderIntroModal
        visible={isIntroVisible}
        content={groupOrderingContent}
        onPrimaryCtaPress={handleGroupOrderIntroPrimaryCtaPress}
        onSecondaryCtaPress={handleGroupOrderIntroSecondaryCtaPress}
        onStartOrder={handleStartGroupOrder}
      />
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getToastBottomOffset(params: Params) {
  const { bottomNoticesCount } = params;

  if (bottomNoticesCount === 0) {
    return 0;
  }

  if (bottomNoticesCount === 1) {
    return theme.spacing['20'];
  }

  return theme.spacing['35'];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SEE_ALL_ITEM_SIZE = 100;
const FLOATING_BANNERS_GAP = theme.spacing['2'];
const DISCLAIMER_ENTERING_ANIMATION = FadeInDown.duration(theme.transitions.short);
const DISCLAIMER_EXITING_ANIMATION = FadeOutDown.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
  },
  bottomBannersContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed,
    pointerEvents: 'box-none',
  },
  bottomBannersContainerXS: {
    right: theme.spacing['4'],
    left: theme.spacing['4'],
    marginBottom: -theme.spacing['1'],
  },
  reorderRailHeader: {
    justifyContent: 'center',
  },
  reorderRailList: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  seeAllItem: {
    flex: 1,
    minWidth: SEE_ALL_ITEM_SIZE,
    marginRight: -SEE_ALL_ITEM_SIZE,
    padding: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerWithOneBottomNotice: {
    paddingBottom: theme.spacing['20'],
  },
  footerWithTwoBottomNotices: {
    paddingBottom: theme.spacing['35'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = { bottomNoticesCount: number };
